import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail, 
  updateProfile,
  signInWithCustomToken
} from 'firebase/auth';
import { doc, setDoc, collection, query, where, getDocs, onSnapshot, deleteDoc, addDoc, updateDoc, getDoc, getDocFromServer } from 'firebase/firestore';
import { 
  LockKeyhole, 
  Mail, 
  Eye, 
  EyeOff, 
  TrendingUp, 
  ShieldCheck, 
  PieChart,
  User,
  ArrowLeft,
  Send,
  Smartphone,
  Check,
  Shield
} from 'lucide-react';

const normalizePhoneBR = (value = '') => {
  let digits = String(value).replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length >= 12) digits = digits.slice(2);
  if (digits.length > 11) digits = digits.slice(-11);
  return digits;
};
const formatPhoneBR = (value = '') => {
  const digits = normalizePhoneBR(value).slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};
const normalizeOtpCode = (value = '') => String(value).replace(/\D/g, '').slice(0, 6);
const buildPhoneDocCandidates = (value = '') => {
  const base = normalizePhoneBR(value);
  const list = new Set();
  if (base) list.add(base);
  if (base && !base.startsWith('55')) list.add(`55${base}`);
  if (base.startsWith('55')) list.add(normalizePhoneBR(base.slice(2)));
  if (base.length === 11 && base[2] === '9') list.add(`${base.slice(0, 2)}${base.slice(3)}`).add(`55${base.slice(0, 2)}${base.slice(3)}`);
  if (base.length === 10) list.add(`${base.slice(0, 2)}9${base.slice(2)}`).add(`55${base.slice(0, 2)}9${base.slice(2)}`);
  return [...list].filter(Boolean);
};
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const sha256Hex = async (value = '') => {
  const normalized = new TextEncoder().encode(String(value));
  const digest = await window.crypto.subtle.digest('SHA-256', normalized);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

export default function Login() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('entrar'); // 'entrar', 'criar', 'recuperar'
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [recoveryMethod, setRecoveryMethod] = useState('email'); // 'email', 'phone'
  const [rememberMe, setRememberMe] = useState(false);
  
  // Estados do formulário
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [telefone, setTelefone] = useState('');
  
  // Estados de feedback do Firebase
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Estados do fluxo de WhatsApp (Passwordless)
  const [etapaZap, setEtapaZap] = useState('pedir_numero');
  const [codigoDigitado, setCodigoDigitado] = useState('');
  const [telefoneZapSolicitado, setTelefoneZapSolicitado] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [dadosToken, setDadosToken] = useState(null);
  const zapRequestUnsubscribeRef = useRef(null);

  useEffect(() => {
    return () => {
      if (zapRequestUnsubscribeRef.current) zapRequestUnsubscribeRef.current();
    };
  }, []);
  
  const getPasswordStrength = (pass) => {
    if (pass.length === 0) return 0;
    if (pass.length < 6) return 1;
    if (pass.length < 8) return 2;
    return 3;
  };

  const strength = getPasswordStrength(password);

  const registrarAcesso = async (uid, origem) => {
    const now = new Date();
    try {
      await updateDoc(doc(db, 'users', uid), {
        horarioEntrada: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        ultimoLoginEm: now.toISOString(),
        atualizadoEm: now.toISOString()
      });
      await addDoc(collection(db, `users/${uid}/logs`), {
        tipo: 'login', acao: 'Entrada no sistema', origem,
        data: now.toISOString(), userAgent: window.navigator.userAgent
      });
    } catch (error) {
      console.warn('Não foi possível registrar acesso:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);

    try {
      if (activeTab === 'entrar') {
        if (!email || !password) throw new Error('Preencha e-mail/username e senha.');
        let emailParaLogin = email;
        if (!email.includes('@')) {
          const q = query(collection(db, 'users'), where('username', '==', email.toLowerCase()));
          const snap = await getDocs(q);
          if (snap.empty) throw new Error('Usuário não encontrado.');
          emailParaLogin = snap.docs[0].data().email;
        }
        const credencial = await signInWithEmailAndPassword(auth, emailParaLogin, password);
        await registrarAcesso(credencial.user.uid, 'senha');
        navigate('/');
      } 
      else if (activeTab === 'criar') {
        if (!nome || !email || !password || !telefone) throw new Error('Preencha todos os campos obrigatórios.');
        if (password !== confirmPassword) throw new Error('As senhas não coincidem.');
        if (password.length < 6) throw new Error('A senha deve ter pelo menos 6 caracteres.');

        const numeroLimpo = normalizePhoneBR(telefone);
        if (numeroLimpo.length < 10 || numeroLimpo.length > 11) throw new Error('Digite um WhatsApp válido com DDD.');

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        await updateProfile(user, { displayName: nome });
        
        await setDoc(doc(db, 'users', user.uid), {
          nome,
          email: email.toLowerCase(),
          telefone: numeroLimpo,
          telefone_normalizado: numeroLimpo,
          telefone_formatado: formatPhoneBR(numeroLimpo),
          criadoEm: new Date().toISOString(),
          status: 'ativo'
        });

        navigate('/');
      } 
      else if (activeTab === 'recuperar') {
        if (recoveryMethod === 'email') {
          if (!email) throw new Error('Informe o e-mail para recuperar.');
          await sendPasswordResetEmail(auth, email);
          setSuccessMsg('Link de recuperação enviado para o seu e-mail!');
        } else {
          if (etapaZap === 'pedir_numero') {
            const numeroLimpo = normalizePhoneBR(telefone);
            if (numeroLimpo.length < 10) throw new Error('Digite um número válido com DDD.');

            let snap = await getDocs(query(collection(db, 'users'), where('telefone_normalizado', '==', numeroLimpo)));
            if (snap.empty) snap = await getDocs(query(collection(db, 'users'), where('telefone', '==', numeroLimpo)));
            if (snap.empty) {
              const usersSnap = await getDocs(collection(db, 'users'));
              const encontrado = usersSnap.docs.find((d) => normalizePhoneBR(d.data().telefone || d.data().telefone_normalizado || '') === numeroLimpo);
              if (encontrado) snap = { empty: false, docs: [encontrado] };
            }

            if (snap.empty) throw new Error('Número não encontrado. Crie uma conta primeiro.');

            const uid = snap.docs[0].id;
            const loginRequestRef = doc(db, 'login_requests', numeroLimpo);
            setTelefoneZapSolicitado(numeroLimpo);

            await setDoc(loginRequestRef, { uid: uid, telefone: numeroLimpo, status: 'solicitado', criadoEm: new Date().toISOString() });
            if (zapRequestUnsubscribeRef.current) zapRequestUnsubscribeRef.current();

            zapRequestUnsubscribeRef.current = onSnapshot(loginRequestRef, (docSnap) => {
              if (!docSnap.exists()) return;
              const data = docSnap.data();
              if (data.status === 'pronto') setDadosToken(data);
              else if (data.status === 'erro') { setDadosToken(null); setError(data.erro || 'Falha ao enviar código.'); }
              else if (data.status === 'bloqueado') { setDadosToken(data); setError('Muitas tentativas incorretas. Solicite um novo código.'); }
              else if (data.status === 'expirado') { setDadosToken(data); setError('Código expirado. Solicite um novo código.'); }
            });

            setEtapaZap('pedir_codigo');
            setSuccessMsg('Código solicitado! Aguarde o recebimento no seu WhatsApp.');
          } else {
            const codigoLimpo = normalizeOtpCode(codigoDigitado);
            if (codigoLimpo.length !== 6) throw new Error('Digite os 6 dígitos do código.');
            const numeroBase = telefoneZapSolicitado || normalizePhoneBR(telefone);
            const docIds = buildPhoneDocCandidates(numeroBase);
            if (!docIds.length) throw new Error('Informe seu WhatsApp novamente.');

            const loadRequest = async () => {
              let latest = null;
              for (let tentativa = 0; tentativa < 15; tentativa += 1) {
                for (const docId of docIds) {
                  const ref = doc(db, 'login_requests', docId);
                  let snap;
                  try { snap = await getDocFromServer(ref); } catch (e) { snap = await getDoc(ref); }
                  if (!snap.exists()) continue;
                  const data = snap.data() || {};
                  latest = { docId, data };
                  const hasCode = Boolean(data.codigoHash || data.codigo);
                  if (data.status === 'pronto' && hasCode && data.token) return latest;
                  if (['erro', 'bloqueado', 'expirado'].includes(data.status)) return latest;
                  if (hasCode && data.token) return latest;
                }
                if (tentativa < 14) await wait(1000);
              }
              return latest;
            };

            const loaded = await loadRequest();
            if (!loaded) throw new Error('Aguarde o recebimento do código no seu WhatsApp.');
            const tokenData = loaded.data || {};
            setDadosToken(tokenData);
            if (tokenData.status === 'erro' || tokenData.status === 'bloqueado' || tokenData.status === 'expirado') throw new Error('Código inválido, bloqueado ou expirado. Tente novamente.');

      // eslint-disable-next-line no-unused-vars
            const maxTentativas = Number(tokenData.maxTentativas || 5);
            let codigoValido = false;
            if (tokenData.codigoHash) codigoValido = (await sha256Hex(codigoLimpo)) === tokenData.codigoHash;
            else if (tokenData.codigo) codigoValido = codigoLimpo === String(tokenData.codigo);

            if (codigoValido) {
              const credencial = await signInWithCustomToken(auth, tokenData.token);
              await registrarAcesso(credencial.user.uid, 'whatsapp');
              await deleteDoc(doc(db, 'login_requests', loaded.docId)).catch(() => {});
              navigate('/');
            } else throw new Error('Código incorreto.');
          }
        }
      }
    } catch (err) {
      console.error(err);
      // Conversão amigável dos erros do Firebase Auth
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('E-mail, username ou senha incorretos.');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('Este e-mail já possui um registo em uso.');
      } else {
        setError(err.message || 'Ocorreu um erro. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-tab-content {
          animation: fadeSlideUp 0.4s ease-out forwards;
        }
      `}</style>

      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-6 md:p-8 font-sans text-slate-800 relative overflow-hidden">
        
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none md:hidden">
          <div className="absolute top-[-5%] left-[-10%] w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-pulse"></div>
          <div className="absolute bottom-[-5%] right-[-10%] w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="w-full max-w-5xl bg-white/80 md:bg-white backdrop-blur-2xl md:backdrop-blur-none rounded-[2rem] shadow-2xl shadow-blue-900/10 overflow-hidden flex flex-col md:flex-row relative z-10 border border-white/60 md:border-none">
          
          <div className="hidden md:flex md:w-5/12 bg-slate-900 p-12 flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
              <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
              <div className="absolute bottom-12 -right-12 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
            </div>

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-16 hover:scale-105 transition-transform origin-left duration-500 cursor-default">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <TrendingUp className="text-white w-6 h-6" />
                </div>
                <span className="text-white text-xl font-bold tracking-wide">LNT Tech</span>
              </div>

              <h1 className="text-4xl font-bold text-white leading-tight mb-6">
                O controlo do seu negócio <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">num só lugar.</span>
              </h1>
              <p className="text-slate-400 text-lg leading-relaxed">
                Plataforma completa para gestão financeira pessoal e empresarial. Decisões inteligentes baseadas em dados reais.
              </p>
            </div>

            <div className="relative z-10 space-y-4">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4 flex items-center gap-4 transform -rotate-2 hover:rotate-0 hover:bg-white/10 transition-all cursor-default shadow-xl shadow-black/20">
                <div className="bg-emerald-500/20 p-3 rounded-lg ring-1 ring-emerald-500/30">
                  <ShieldCheck className="text-emerald-400 w-6 h-6" />
                </div>
                <div>
                  <p className="text-white font-medium">Segurança Bancária</p>
                  <p className="text-slate-400 text-sm">Encriptação de ponta a ponta</p>
                </div>
              </div>
              
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4 flex items-center gap-4 transform translate-x-6 rotate-1 hover:rotate-0 hover:bg-white/10 transition-all cursor-default shadow-xl shadow-black/20">
                <div className="bg-blue-500/20 p-3 rounded-lg ring-1 ring-blue-500/30">
                  <PieChart className="text-blue-400 w-6 h-6" />
                </div>
                <div>
                  <p className="text-white font-medium">Relatórios Precisos</p>
                  <p className="text-slate-400 text-sm">Visão clara do seu fluxo de caixa</p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full md:w-7/12 p-6 sm:p-10 lg:p-16 flex flex-col justify-center bg-transparent md:bg-white relative">
            
            <div className="md:hidden flex flex-col items-center gap-3 mb-10 justify-center mt-2">
               <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl shadow-blue-600/30 ring-4 ring-white/50">
                  <TrendingUp className="text-white w-8 h-8" />
                </div>
                <div className="text-center">
                  <span className="text-slate-900 text-3xl font-black tracking-tight">LNT Tech</span>
                  <p className="text-blue-600 text-[11px] font-bold tracking-[0.25em] uppercase mt-1">Finance</p>
                </div>
            </div>

            <div className="max-w-md w-full mx-auto">
              <div className="text-center md:text-left mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {activeTab === 'entrar' && 'Bem-vindo de volta'}
                  {activeTab === 'criar' && 'Crie a sua conta'}
                  {activeTab === 'recuperar' && (recoveryMethod === 'email' ? 'Recuperar acesso' : 'Acesso via WhatsApp')}
                </h2>
                <p className="text-slate-500">
                  {activeTab === 'entrar' && 'Aceda ao seu sistema de gestão financeira.'}
                  {activeTab === 'criar' && 'Comece a gerir as suas finanças hoje mesmo.'}
                  {activeTab === 'recuperar' && (recoveryMethod === 'email' ? 'Enviaremos um link de recuperação para o seu email.' : 'Receba um código no WhatsApp para entrar sem senha.')}
                </p>
              </div>

              {activeTab !== 'recuperar' && (
                <div className="flex p-1.5 bg-slate-100/80 rounded-xl mb-8 border border-slate-200/60">
                  <button
                    onClick={() => { setActiveTab('entrar'); setError(''); setSuccessMsg(''); }}
                    className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 ${
                      activeTab === 'entrar' 
                        ? 'bg-white text-blue-600 shadow-sm ring-1 ring-slate-900/5' 
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                    }`}
                  >
                    Entrar
                  </button>
                  <button
                    onClick={() => { setActiveTab('criar'); setError(''); setSuccessMsg(''); }}
                    className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 ${
                      activeTab === 'criar' 
                        ? 'bg-white text-blue-600 shadow-sm ring-1 ring-slate-900/5' 
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                    }`}
                  >
                    Criar Conta
                  </button>
                </div>
              )}

              <div key={activeTab} className="animate-tab-content">
                <form className="space-y-5" onSubmit={handleSubmit}>
                  
                  {error && (
                    <div className="p-3 bg-red-50 text-red-600 border border-red-100 rounded-xl text-sm font-medium">
                      {error}
                    </div>
                  )}
                  {successMsg && (
                    <div className="p-3 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-xl text-sm font-medium">
                      {successMsg}
                    </div>
                  )}

                  {activeTab === 'recuperar' ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => { setRecoveryMethod('email'); setError(''); setSuccessMsg(''); }}
                          className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-300 ${
                            recoveryMethod === 'email'
                              ? 'border-blue-600 bg-blue-50/50 text-blue-700 shadow-sm transform scale-[1.02]'
                              : 'border-slate-100 bg-white text-slate-500 hover:border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <div className={`p-3 rounded-full mb-3 transition-colors ${recoveryMethod === 'email' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                            <Mail className="w-6 h-6" />
                          </div>
                          <span className="text-sm font-bold">Por E-mail</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => { setRecoveryMethod('phone'); setError(''); setSuccessMsg(''); setEtapaZap('pedir_numero'); setCodigoDigitado(''); }}
                          className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-300 ${
                            recoveryMethod === 'phone'
                              ? 'border-blue-600 bg-blue-50/50 text-blue-700 shadow-sm transform scale-[1.02]'
                              : 'border-slate-100 bg-white text-slate-500 hover:border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <div className={`p-3 rounded-full mb-3 transition-colors ${recoveryMethod === 'phone' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                            <Smartphone className="w-6 h-6" />
                          </div>
                          <span className="text-sm font-bold">WhatsApp</span>
                        </button>
                      </div>

                      {recoveryMethod === 'email' ? (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700 block">
                            E-mail registado <span className="text-red-500">*</span>
                          </label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-blue-500">
                              <Mail className="h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                            </div>
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="block w-full pl-12 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 focus:bg-white transition-all outline-none"
                              placeholder="exemplo@empresa.com"
                            />
                          </div>
                        </div>
                      ) : (
                        etapaZap === 'pedir_numero' ? (
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium text-slate-700 block">
                              Número de WhatsApp <span className="text-red-500">*</span>
                            </label>
                            <div className="relative group">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-blue-500">
                              <Smartphone className="h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                              </div>
                              <input
                                type="tel"
                                value={telefone}
                                onChange={(e) => setTelefone(formatPhoneBR(e.target.value))}
                                className="block w-full pl-12 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 focus:bg-white transition-all outline-none"
                                placeholder="(00) 90000-0000"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium text-slate-700 block">
                              Código de 6 Dígitos <span className="text-red-500">*</span>
                            </label>
                            <div className="relative group">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-blue-500">
                                <LockKeyhole className="h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                              </div>
                              <input
                                type="text"
                                maxLength={6}
                                value={codigoDigitado}
                                onChange={(e) => setCodigoDigitado(normalizeOtpCode(e.target.value))}
                                className="block w-full pl-12 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 focus:bg-white transition-all outline-none tracking-[0.5em] text-center font-bold"
                                placeholder="000000"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => { setEtapaZap('pedir_numero'); setError(''); setSuccessMsg(''); setDadosToken(null); setCodigoDigitado(''); }}
                              className="w-full flex items-center justify-end gap-1 mt-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              Alterar número
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <>
                      {activeTab === 'criar' && (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700 block">Nome Completo <span className="text-red-500">*</span></label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <User className="h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                            </div>
                            <input 
                              type="text" 
                              value={nome}
                              onChange={(e) => setNome(e.target.value)}
                              className="block w-full pl-12 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 focus:bg-white transition-all outline-none shadow-sm"
                              placeholder="João da Silva"
                            />
                          </div>
                        </div>
                      )}

                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-slate-700 block">
                          E-mail <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Mail className="h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                          </div>
                          <input 
                            type="email" 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="block w-full pl-12 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 focus:bg-white transition-all outline-none shadow-sm"
                            placeholder="exemplo@empresa.com"
                          />
                        </div>
                      </div>

                      {activeTab === 'criar' && (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700 block">WhatsApp (com DDD) <span className="text-red-500">*</span></label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <Smartphone className="h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                            </div>
                            <input 
                              type="tel" 
                              value={telefone}
                              onChange={(e) => setTelefone(formatPhoneBR(e.target.value))}
                              className="block w-full pl-12 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 focus:bg-white transition-all outline-none shadow-sm"
                              placeholder="(00) 90000-0000"
                            />
                          </div>
                        </div>
                      )}

                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-slate-700 block">Senha <span className="text-red-500">*</span></label>
                          {activeTab === 'entrar' && (
                            <button 
                              type="button" 
                              onClick={() => { setActiveTab('recuperar'); setRecoveryMethod('email'); setError(''); setSuccessMsg(''); }}
                              className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                            >
                              Esqueceu a palavra-passe?
                            </button>
                          )}
                        </div>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <LockKeyhole className="h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                          </div>
                          <input 
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="block w-full pl-12 pr-12 py-3.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 focus:bg-white transition-all outline-none shadow-sm"
                            placeholder="••••••••"
                          />
                          <button 
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-blue-600 transition-colors"
                          >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                        
                        {activeTab === 'criar' && password.length > 0 && (
                          <div className="pt-3 flex gap-1">
                            <div className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${strength >= 1 ? 'bg-red-500' : 'bg-slate-200'}`}></div>
                            <div className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${strength >= 2 ? 'bg-amber-500' : 'bg-slate-200'}`}></div>
                            <div className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${strength >= 3 ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>
                          </div>
                        )}
                      </div>

                      {activeTab === 'entrar' && (
                        <div className="flex items-center mt-2">
                          <button 
                            type="button"
                            onClick={() => setRememberMe(!rememberMe)}
                            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors group"
                          >
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${rememberMe ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'}`}>
                              {rememberMe && <Check className="w-3.5 h-3.5 text-white" />}
                            </div>
                            Lembrar-me neste dispositivo
                          </button>
                        </div>
                      )}

                      {activeTab === 'criar' && (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700 block">Confirmar Senha <span className="text-red-500">*</span></label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <LockKeyhole className="h-5 w-5 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                            </div>
                            <input 
                              type={showConfirmPassword ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="block w-full pl-12 pr-12 py-3.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 focus:bg-white transition-all outline-none shadow-sm"
                              placeholder="••••••••"
                            />
                            <button 
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-blue-600 transition-colors"
                            >
                              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <button 
                    type="submit"
                    disabled={loading}
                    className="w-full mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 px-4 rounded-xl shadow-lg shadow-blue-600/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                    ) : (
                      <>
                        {activeTab === 'entrar' && 'Aceder à Plataforma'}
                        {activeTab === 'criar' && 'Criar Conta Agora'}
                        {activeTab === 'recuperar' && (
                          <>
                            <Send className="w-4 h-4" />
                            {recoveryMethod === 'email' 
                              ? 'Enviar Link por E-mail' 
                              : etapaZap === 'pedir_numero' 
                                ? 'Enviar Código no WhatsApp' 
                                : 'Validar Código e Acessar'
                            }
                          </>
                        )}
                      </>
                    )}
                  </button>

                  {activeTab === 'recuperar' && (
                    <button 
                      type="button" 
                      onClick={() => { setActiveTab('entrar'); setError(''); setSuccessMsg(''); }}
                      className="w-full flex items-center justify-center gap-2 mt-4 py-2.5 text-sm font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Voltar para o login
                    </button>
                  )}
                </form>

                {activeTab !== 'recuperar' && (
                  <>
                    <div className="mt-8 mb-6 relative flex items-center">
                      <div className="flex-grow border-t border-slate-200"></div>
                      <span className="flex-shrink-0 mx-4 text-slate-400 text-sm font-medium">ou acesso rápido</span>
                      <div className="flex-grow border-t border-slate-200"></div>
                    </div>

                    <button 
                      type="button"
                      onClick={() => { setActiveTab('recuperar'); setRecoveryMethod('phone'); setEtapaZap('pedir_numero'); setError(''); setSuccessMsg(''); }}
                      className="w-full flex items-center justify-center gap-2 bg-emerald-50 text-emerald-600 border-2 border-emerald-500 hover:bg-emerald-500 hover:text-white font-semibold py-3.5 px-4 rounded-xl transition-all hover:shadow-md hover:shadow-emerald-500/20 transform active:scale-[0.98]"
                    >
                      <Smartphone className="w-5 h-5 -ml-1" />
                      Acessar sem senha (WhatsApp)
                    </button>
                  </>
                )}
                
                <div className="mt-8 flex items-center justify-center gap-1.5 text-slate-400 text-xs font-medium">
                  <Shield className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Protegido com encriptação de nível bancário (SSL)</span>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </div>
    </>
  );
}